// src/pages/admin/depositMatchUtils.js
import { addDaysISO } from "@utils/dateRanges";
import { suggestMatches, suggestionLabel } from "./depositAutoMatch";

export const formatCad = (n) =>
  Number(n || 0).toLocaleString("en-CA", { style: "currency", currency: "CAD" });

// Signed money with a real minus sign, e.g. "−$45.00" / "+$12.00".
export const formatSignedCad = (n) => {
  const v = Number(n) || 0;
  if (v === 0) return formatCad(0);
  return `${v < 0 ? "−" : "+"}${formatCad(Math.abs(v))}`;
};

export const addDays = addDaysISO;

const cents = (n) => Math.round((Number(n) || 0) * 100);
const sum = (lines) => lines.reduce((n, l) => n + l.amount, 0);

export const daysBetween = (fromIso, toIso) =>
  Math.round((new Date(`${toIso}T00:00:00`) - new Date(`${fromIso}T00:00:00`)) / 86400000);

export const fmtShortDay = (iso) =>
  iso
    ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-CA", {
        weekday: "short",
        month: "short",
        day: "numeric",
      })
    : "";

// How long after a deposit its bank line may post and still pair with it.
export const MATCH_WINDOW_DAYS = 3;
// How long BEFORE a deposit its bank line may be dated (a wrong bank date).
export const MATCH_BACK_DAYS = 3;

/**
 * Pair one clinic's deposit lines with its bank lines.
 *
 * Saved (manual) matches come first: every line they name is taken out of the
 * pool and shown together as one "matched" row, however many lines each side
 * has. A saved line that falls outside the loaded dates is still shown, from
 * the copy stored on the match.
 *
 * What is left goes to `suggestMatches` (depositAutoMatch.js), which proposes
 * pairs by amount + memo/description. Proposals are "suggested"
 * rows — nothing is saved until a person accepts one. Bank lines that are not
 * patient money (transfers, loans) come back as "excluded". Anything still
 * left is pending.
 *
 *   deposits: [{ id, date, method, amount }]       id = "<date>:<field>"
 *   bank:     [{ id, date, description, amount, title }]
 *   saved:    [{ id, lines: [{ side, key, date, amount, description }] }]
 * Returns rows: { id, status, deposits: [], banks: [], variance, matchId? }
 *   variance = bank − deposit; non-zero on saved matches made with unequal
 *   totals and on `near` suggestions.
 *   status: "matched" | "suggested" | "variance" | "depositPending" |
 *           "bankPending" | "excluded"
 *   matchId is set on saved matches only (they can be unmatched); suggested
 *   rows carry rule / confidence / label.
 */
export function matchDeposits(deposits, bank, saved = []) {
  const depositById = new Map(deposits.map((d) => [d.id, d]));
  const bankById = new Map(bank.map((b) => [b.id, b]));
  const rows = [];

  for (const m of saved) {
    const ds = [];
    const bs = [];
    for (const line of m.lines || []) {
      const amount = Number(line.amount) || 0;
      if (line.side === "deposit") {
        ds.push(
          depositById.get(line.key) ?? {
            id: line.key,
            date: line.date,
            method: line.description || line.key,
            amount,
          },
        );
        depositById.delete(line.key);
      } else {
        bs.push(
          bankById.get(line.key) ?? {
            id: line.key,
            date: line.date,
            description: line.description || line.key,
            amount,
          },
        );
        bankById.delete(line.key);
      }
    }
    rows.push({
      id: `match-${m.id}`,
      matchId: m.id,
      auto: !!m.auto,
      status: "matched",
      deposits: ds,
      banks: bs,
      variance: (cents(sum(bs)) - cents(sum(ds))) / 100,
    });
  }

  // Everything not in a saved match goes to the suggestion engine
  // (depositAutoMatch.js): amount + memo/description.
  const auto = suggestMatches([...depositById.values()], [...bankById.values()], {
    windowDays: MATCH_WINDOW_DAYS,
    backDays: MATCH_BACK_DAYS,
  });
  auto.suggestions.forEach((sug, i) =>
    rows.push({
      id: `sug-${i}-${sug.deposits[0]?.id ?? sug.banks[0]?.id}`,
      status: "suggested",
      deposits: sug.deposits,
      banks: sug.banks,
      variance: (cents(sum(sug.banks)) - cents(sum(sug.deposits))) / 100,
      rule: sug.rule,
      confidence: sug.confidence,
      label: suggestionLabel(sug),
    }),
  );
  for (const dep of auto.deposits) {
    rows.push({ id: dep.id, status: "depositPending", deposits: [dep], banks: [], variance: 0 });
  }
  for (const b of auto.bank) {
    rows.push({ id: b.id, status: "bankPending", deposits: [], banks: [b], variance: 0 });
  }
  for (const b of auto.excluded) {
    rows.push({ id: b.id, status: "excluded", deposits: [], banks: [b], variance: 0 });
  }

  // Suggested rows first (they need a decision), then newest first by the
  // row's earliest line on either side.
  const rowDate = (r) => r.deposits[0]?.date || r.banks[0]?.date || "";
  const rank = (r) => (r.status === "suggested" ? 0 : 1);
  return rows.sort(
    (a, b) => rank(a) - rank(b) || (rowDate(a) < rowDate(b) ? 1 : rowDate(a) > rowDate(b) ? -1 : 0),
  );
}

/** Per-practice money and counts for the stats strip and header. */
export function practiceStats(rows) {
  const s = {
    deposits: 0,
    depositCount: 0,
    bank: 0,
    bankCount: 0,
    matched: 0,
    matchedPairs: 0,
    suggested: 0,
    suggestedCount: 0,
    excluded: 0,
    excludedCount: 0,
    variance: 0,
    variancePairs: 0,
    varianceDeposited: 0,
    depositPending: 0,
    depositPendingCount: 0,
    bankPending: 0,
    bankPendingCount: 0,
  };
  for (const r of rows) {
    // Transfers / loans are not patient money: kept out of every total.
    if (r.status === "excluded") {
      s.excluded += sum(r.banks);
      s.excludedCount += r.banks.length;
      continue;
    }
    const dep = sum(r.deposits);
    s.deposits += dep;
    s.depositCount += r.deposits.length;
    s.bank += sum(r.banks);
    s.bankCount += r.banks.length;
    if (r.status === "matched") {
      s.matched += dep;
      s.matchedPairs += 1;
      // Saved with unequal totals: also counted under Variances.
      if (r.variance) {
        s.variance += r.variance;
        s.variancePairs += 1;
        s.varianceDeposited += dep;
      }
    } else if (r.status === "suggested") {
      s.suggested += dep;
      s.suggestedCount += 1;
    } else if (r.status === "variance") {
      s.variance += r.variance;
      s.variancePairs += 1;
      s.varianceDeposited += dep;
    } else if (r.status === "depositPending") {
      s.depositPending += dep;
      s.depositPendingCount += r.deposits.length;
    } else if (r.status === "bankPending") {
      s.bankPending += sum(r.banks);
      s.bankPendingCount += r.banks.length;
    }
  }
  return s;
}

// Filter tabs → which row statuses they show.
export const FILTERS = {
  needs: ["suggested", "variance", "depositPending", "bankPending"],
  suggested: ["suggested"],
  depositPending: ["depositPending"],
  bankPending: ["bankPending"],
  matched: ["matched"],
  // Nothing is posted from this page yet; the tab holds the Bank feeds button.
  toPost: [],
  all: null,
};
